import { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Box, Typography, IconButton, TextField, InputAdornment,
  Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Snackbar, Alert, Button,
} from '@mui/material';
import { Refresh, Search, ArrowBack, Build } from '@mui/icons-material';

/* ── Tokens ── */
const C = {
  bg: '#f1f5f9',
  card: '#ffffff',
  border: '#e2e8f0',
  text: '#0f172a',
  sec: '#475569',
  muted: '#94a3b8',
  blue: '#1e40af',
};

const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};

/* Returns today's date as YYYY-MM-DD for input[type=date] */
const todayStr = () => new Date().toISOString().split('T')[0];
/* Returns first day of current month as YYYY-MM-DD */
const firstDayOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };

/* ════════════════════════════════════════
   INSTALLATION SCHEDULE LIST
════════════════════════════════════════ */
export default function InstallationScheduleList() {
  useSessionValidation();
  const history = useHistory();
  const { companyId, branchId, token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(firstDayOfMonth());
  const [toDate, setToDate] = useState(todayStr());
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });

  const load = useCallback(async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      const res = await fetchData(
        'ExecutivedashboardApi',
        `/MonthlyInstallation/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const list = res?.response_data || res || [];
      setData(Array.isArray(list) ? list : []);
    } catch {
      setSnack({ open: true, msg: 'Failed to load installation schedules', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, token, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(
      (r) =>
        (r.CustomerName || '').toLowerCase().includes(q) ||
        (r.BillNumber || '').toLowerCase().includes(q) ||
        (r.ProductName || r.ItemName || '').toLowerCase().includes(q) ||
        (r.ScheduleDate || '').includes(q)
    );
  }, [data, search]);

  if (loading) return <LogoLoader open message="Loading installations..." />;

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', pb: 5 }}>

      {/* ── Header ── */}
      <Box sx={{
        bgcolor: '#fff', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: '2px solid #1d4ed8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <IconButton size="small" onClick={() => history.goBack()} sx={{ color: '#1e40af', p: 0.5 }}>
          <ArrowBack sx={{ fontSize: 18 }} />
        </IconButton>
        <Build sx={{ fontSize: 18, color: '#1d4ed8' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', flex: 1 }}>
          Installation <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#475569' }}>Schedule List</span>
        </Typography>
        <Chip label={`${filtered.length} records`} size="small"
          sx={{ bgcolor: '#eff6ff', color: '#1e40af', fontWeight: 700, fontSize: '0.7rem' }} />
        <IconButton size="small" onClick={load} sx={{ color: '#1e40af', p: 0.5 }}>
          <Refresh sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ px: 1.5, mt: 1.5 }}>

        {/* ── Single row: Search + From + To + Go ── */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search customer, bill no, item..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 17, color: C.muted }} /></InputAdornment> }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.82rem', bgcolor: C.card, '& fieldset': { borderColor: C.border } },
            }}
          />
          <TextField
            label="From"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: toDate }}
            sx={{
              width: 145,
              '& .MuiInputBase-input': { fontSize: '0.75rem', py: '6px' },
              '& .MuiInputLabel-root': { fontSize: '0.72rem' },
              '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: C.card, '& fieldset': { borderColor: C.border } },
            }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: fromDate }}
            sx={{
              width: 145,
              '& .MuiInputBase-input': { fontSize: '0.75rem', py: '6px' },
              '& .MuiInputLabel-root': { fontSize: '0.72rem' },
              '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: C.card, '& fieldset': { borderColor: C.border } },
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={load}
            sx={{
              bgcolor: C.blue, color: '#fff', fontWeight: 700, fontSize: '0.75rem',
              borderRadius: '10px', textTransform: 'none', px: 2, py: '6px',
              flexShrink: 0, '&:hover': { bgcolor: '#1e3a8a' },
            }}
          >
            Go
          </Button>
        </Box>

        <Box sx={{ bgcolor: C.card, borderRadius: 2.5, border: `1px solid ${C.border}`, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['#', 'Customer', 'Bill No', 'Schedule Date', 'Item / Product', 'Status'].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.7rem', color: C.blue, textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', py: 1 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: C.muted, fontSize: '0.85rem' }}>
                      No installation schedules found
                    </TableCell>
                  </TableRow>
                ) : filtered.map((row, i) => (
                  <TableRow key={row.Id ?? i} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                    <TableCell sx={{ fontSize: '0.72rem', color: C.muted }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.CustomerName || '-'}
                       <p>{row.ContactNumber || row.CustomerContactNumber || '-'}</p>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, color: C.blue, whiteSpace: 'nowrap' }}>
                      {row.BillNo || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {fmtDate(row.ScheduleDate || row.InstallationDate)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.sec }}>
                      {row.ProductName || row.ItemName || '-'}
                    </TableCell>
                    {/* <TableCell>
                      <Chip label={row.InstallationStatus || 'Pending'} size="small"
                        sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700,
                          bgcolor: row.InstallationStatus === 'Completed' ? '#dcfce7' : '#fef9c3',
                          color:   row.InstallationStatus === 'Completed' ? '#38e477ff' : '#ebb43eff' }} />
                    </TableCell> */}

                    <Chip
                      label={row.InstallationStatus || "Pending"}
                      sx={{
                        bgcolor:
                          row.InstallationStatus === "Completed"
                            ? "#dcfce7 !important"
                            : "#fef9c3 !important",
                        color:
                          row.InstallationStatus === "Completed"
                            ? "#16a34a !important"
                            : "#ca8a04 !important",
                      }}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(p => ({ ...p, open: false }))} sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
